// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useIsMobile } from 'hooks/useIsMobile';

export const screenshottingSupported = (): boolean => {
  const isMobile = useIsMobile();
  return !isMobile && 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;
};

export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  blobOptions?: { type?: string; quality?: unknown }
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, blobOptions?.type, blobOptions?.quality));
}

export async function takeScreenshot(): Promise<HTMLCanvasElement> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stream = await (navigator.mediaDevices as any).getDisplayMedia({
    video: true,
    audio: false
  });

  // ensure the dialog for choosing window has gone away and hence won't show up in the screenshot
  await sleep(2000);

  const video = document.createElement('video');
  video.srcObject = stream;
  video.play();

  return new Promise((resolve) => {
    video.addEventListener(
      'canplay',
      () => {
        const canvas = draw(video);
        resolve(canvas);
      },
      { once: true }
    );
  });
}

function draw(video: HTMLVideoElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  video.width = canvas.width = video.videoWidth;
  video.height = canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (video.srcObject as any)?.getTracks().forEach((track) => track.stop());
  video.srcObject = null;

  return canvas;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
